import React from 'react';
import { Link, Navigate, useParams } from 'react-router';
import styled from 'styled-components';
import SEO from '../../components/seo/SEO';
import SeoPageIntro from '../../components/seo/SeoPageIntro';
import { getSeoLandingPage } from '../../constants/seoLandingPages';
import { buildBreadcrumbSchema, buildFaqSchema } from '../../utils/seoStructuredData';

const Page = styled.main`
  max-width: 920px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
`;

const Content = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const Section = styled.section`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1.35rem;

  h2 {
    margin: 0 0 0.65rem;
    color: var(--text-primary);
    font-size: 1.25rem;
  }

  p {
    margin: 0;
    color: var(--text-secondary);
    line-height: 1.7;
  }
`;

const LinkGrid = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Cta = styled(Link)`
  text-decoration: none;
  color: white;
  background: var(--primary-color);
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-weight: 700;

  &:hover {
    opacity: 0.9;
  }
`;

const Faq = styled.details`
  border-top: 1px solid var(--border-color);
  padding: 1rem 0;

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }

  summary {
    cursor: pointer;
    color: var(--text-primary);
    font-weight: 700;
  }

  p {
    margin: 0.65rem 0 0;
  }
`;

export default function SeoLandingPage({ slug: configuredSlug, arabic = false }) {
  const { slug: routeSlug } = useParams();
  const landing = getSeoLandingPage(configuredSlug || routeSlug);

  if (!landing) return <Navigate to="/" replace />;

  const copy = arabic ? landing.arabic : landing.english;
  const path = `${arabic ? '/ar' : ''}/${landing.slug}`;
  const alternatePath = `${arabic ? '' : '/ar'}/${landing.slug}`;
  const language = arabic ? 'ar' : 'en';
  const jsonLd = [
    buildBreadcrumbSchema([
      { name: arabic ? 'الرئيسية' : 'Home', url: '/' },
      { name: copy.h1, url: path },
    ]),
    buildFaqSchema(copy.faqs),
  ];

  return (
    <Page dir={arabic ? 'rtl' : 'ltr'} lang={language}>
      <SEO
        title={copy.title}
        description={copy.description}
        keywords={copy.keywords}
        url={path}
        locale={arabic ? 'ar_EG' : 'en_EG'}
        alternates={[
          { hrefLang: language, href: path },
          { hrefLang: arabic ? 'en' : 'ar', href: alternatePath },
          { hrefLang: 'x-default', href: `/${landing.slug}` },
        ]}
        jsonLd={jsonLd}
      />
      <SeoPageIntro h1={copy.h1} description={copy.intro} />
      <Content>
        {copy.sections.map((section) => (
          <Section key={section.heading}>
            <h2>{section.heading}</h2>
            <p>{section.body}</p>
          </Section>
        ))}
        <Section>
          <h2>{arabic ? 'ابدأ التصفح أو البيع' : 'Start browsing or selling'}</h2>
          <LinkGrid aria-label={arabic ? 'روابط تصفح فلي' : 'Browse Flea links'}>
            {copy.ctaLinks.map((link) => <Cta key={link.to} to={link.to}>{link.label}</Cta>)}
          </LinkGrid>
        </Section>
        <Section>
          <h2>{arabic ? 'أسئلة شائعة' : 'Frequently asked questions'}</h2>
          {copy.faqs.map((faq) => (
            <Faq key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </Faq>
          ))}
        </Section>
      </Content>
    </Page>
  );
}
